/**
 * D-13 login-time setup reminder (contacts handoff §2.4 — operator-set
 * 2026-07-16): while the minimum profile is incomplete, re-display on
 * login with a ≥6h interval; the explicit "não me lembrar" toggle
 * suppresses it and auto-resets after 7 days. Timestamps are persisted
 * RUNTIME-side (`setup.status` / `setup.reminder_shown`) so devices
 * agree — never localStorage-only. Renders a slim dismissible banner.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { shouldGateSetup, shouldShowSetupReminder } from "@/lib/contacts-logic";
import {
  selectWizardOnScreen,
  useSetupWizardPresenceStore,
} from "@/stores/useSetupWizardPresenceStore";

export function SetupReminderGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [visible, setVisible] = useState(false);
  /** One check per login/session — not per reconnect flap. */
  const checked = useRef(false);

  /** Live "the wizard is mounted" signal (see `useSetupWizardPresenceStore`).
   *  Read two ways on purpose:
   *   - `wizardOnScreenRef` at DECISION time, because the status promise
   *     resolves asynchronously and the check must see the value as of then;
   *   - `wizardOnScreen` at RENDER time, which also covers the wizard mounting
   *     AFTER the banner was already shown. The render guard is the one that
   *     cannot be out of date. */
  const wizardOnScreen = useSetupWizardPresenceStore(selectWizardOnScreen);
  const wizardOnScreenRef = useRef(wizardOnScreen);
  useEffect(() => {
    wizardOnScreenRef.current = wizardOnScreen;
  }, [wizardOnScreen]);

  useEffect(() => {
    if (checked.current || !rpc || connectionState !== "connected") return;
    checked.current = true;
    rpc
      .setupStatus()
      .then((status) => {
        // The blocking SetupGate owns the surface while required steps are
        // outstanding — the soft banner stays silent then.
        if (shouldGateSetup(status)) return;
        // ⚠ …but that guard only covers the BLOCKING gate. A user whose
        // outstanding steps are all OPTIONAL passes it, so the banner used to
        // fire while they were standing on the wizard it points at — its
        // "Completar agora" aimed at the thing already open (operator-reported
        // 2026-08-10). The two questions — "is the blocking gate active?" and
        // "is the wizard on screen?" — coincided until such a user existed.
        // Ask the second one directly.
        if (wizardOnScreenRef.current) return;
        if (shouldShowSetupReminder(status, Date.now())) {
          setVisible(true);
          // Record the display runtime-side so other devices honor the 6h window.
          void rpc.setupReminderShown().catch(() => undefined);
        }
      })
      .catch(() => undefined); // backend pending → no reminder, no noise
  }, [rpc, connectionState]);

  // Render-time suppression: covers the wizard mounting AFTER the banner
  // was shown. Unmounting the wizard lets it reappear, which is right — the
  // user left the surface without finishing.
  if (!visible || wizardOnScreen) return null;
  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-sm rounded-lg border border-amber-400/60 bg-amber-50 p-3 shadow-lg dark:bg-amber-950/90"
      data-testid="setup_reminder_banner"
    >
      <p className="mb-2 text-sm text-amber-900 dark:text-amber-100">
        {t(
          "onboarding.reminderText",
          "Seu assistente ainda não está 100% configurado — faltam itens do perfil mínimo (e-mail, Telegram…).",
        )}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            setVisible(false);
            navigate("/identity?onboarding=1");
          }}
          className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white"
          data-testid="setup_reminder_open"
        >
          {t("onboarding.reminderCta", "Completar agora")}
        </button>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="rounded px-3 py-1 text-xs text-amber-800 hover:underline dark:text-amber-200"
          data-testid="setup_reminder_dismiss"
        >
          {t("onboarding.reminderLater", "Depois")}
        </button>
      </div>
    </div>
  );
}
